/**
 * Keyboard shortcuts for track replay.
 * Space=play/pause, arrows=step, +/-=speed.
 * Only active when replay panel has frames loaded.
 */

import { useEffect } from 'react';
import { useReplayStore } from '@/stores/replay-store';
import { REPLAY_SPEEDS } from '@/constants/drawing';

/** Attach keyboard shortcuts for replay controls */
export function useReplayControls(): void {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      const store = useReplayStore.getState();
      if (store.frames.length === 0) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          store.playing ? store.pause() : store.play();
          break;
        case 'ArrowRight':
          e.preventDefault();
          store.seekTo(Math.min(store.currentIndex + 1, store.frames.length - 1));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          store.seekTo(Math.max(store.currentIndex - 1, 0));
          break;
        case '+':
        case '=':
          e.preventDefault();
          cycleSpeed(store.speed, 1);
          break;
        case '-':
          e.preventDefault();
          cycleSpeed(store.speed, -1);
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}

/** Cycle through available speeds */
function cycleSpeed(current: number, direction: number): void {
  const speeds = REPLAY_SPEEDS;
  const currentIdx = speeds.indexOf(current as typeof speeds[number]);
  const nextIdx = Math.max(0, Math.min(speeds.length - 1, currentIdx + direction));
  useReplayStore.getState().setSpeed(speeds[nextIdx]!);
}
